import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { catchError, concatMap, delay } from 'rxjs/operators';
import { AppService } from '../_shared/services/app.service';
import { PermissionService } from '../_shared/services/permission.service';
import { ProductsCategoryService } from '../_shared/services/products-category.service';
import { ProductsService } from '../_shared/services/products.service';
import { SelectedCustomerService } from '../_shared/services/selected-customer.service';
import { SnackBarService } from '../_shared/services/snack-bar.service';
import { OverviewDataSource } from './overview-data-source';
import { CopyQuoteParams, OverviewDataService, QuoteItem, SearchParams, TemplateItem } from './overview.data.service';
import { QuoteDiscountsService } from '../_shared/services/quote-discounts.service';
import { environment } from '../../environments/environment';
import { QuoteService } from '../_shared/services/quote.service';
import { openQuoteCreationDialog } from '../_shared/components/quote-creation-dialog/quote-creation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { EnvironmentDiffService } from '../_shared/services/environment-diff.service';
import { ProductListService } from '../_shared/components/product-list/product-list.service';
import {
    ConvertQuoteEvent,
    CopyQuoteEvent,
    DeleteQuoteEvent,
    ViewQuoteEvent,
} from '../_shared/components/overview-table/overview-table.component';
import { QuoteStatus } from '../_shared/models/quote.model';
import { EditTemplateEvent, TemplateEvent } from '../_shared/components/templates-table/templates-table.component';
import { SearchEvent } from '../_shared/components/search-and-filter/search-and-filter.component';

@Component({
    selector: 'app-overview',
    templateUrl: './overview.component.html',
    styleUrls: ['./overview.component.scss'],
})
export class OverviewComponent implements OnInit {
    private isQuotesLoading = true;
    private isTemplatesLoading = true;
    public loaderText: string;
    public overviewTableDataSource: OverviewDataSource<QuoteItem, SearchParams>;
    public templatesDataSource: OverviewDataSource<TemplateItem, SearchParams>;

    public selectedTab: 'offers' | 'templates' = 'offers';

    public foerderProfiUrl: string;

    constructor(
        public dataService: OverviewDataService,
        private snackBarService: SnackBarService,
        private translateService: TranslateService,
        private productsService: ProductsService,
        private router: Router,
        private route: ActivatedRoute,
        private dialog: MatDialog,
        private permissionService: PermissionService,
        private selectedCustomerService: SelectedCustomerService,
        private quoteDiscountsService: QuoteDiscountsService,
        public appService: AppService,
        private categoryService: ProductsCategoryService,
        private environmentDiffService: EnvironmentDiffService,
        private quoteService: QuoteService,
        private productListService: ProductListService
    ) {}

    public ngOnInit() {
        this.appService.appTitle$.next('');
        this.handleRoute();
        this.foerderProfiUrl = environment.http.foerderProfiUrl;
        this.quoteService.reset();
        this.productListService.reset();
        this.selectedCustomerService.clearSelectedCustomer();
        this.productsService.clearPriceSummaryCache();
        this.categoryService.clearCategoriesCache();
        this.environmentDiffService.clearCache();

        this.setLoaderText('OVERVIEW.LOADER.LOADING_OFFERS');
        this.overviewTableDataSource = new OverviewDataSource<QuoteItem, SearchParams>(
            this.appService,
            this.selectedCustomerService,
            (p) => this.dataService.searchQuotes(p)
        );
        this.overviewTableDataSource.loading$.subscribe((loading) => {
            this.isQuotesLoading = loading;
        });
        this.templatesDataSource = new OverviewDataSource<TemplateItem, SearchParams>(
            this.appService,
            this.selectedCustomerService,
            (p) => this.dataService.searchTemplates(p)
        );
        this.templatesDataSource.loading$.subscribe((loading) => (this.isTemplatesLoading = loading));
        this.appService.salesOrg$.subscribe(() => {
            this.getQuoteList();
            this.getTemplateList();
        });
    }

    handleRoute() {
        const firstChild = this.route.snapshot.children[0];
        if (firstChild.url.some(({ path }) => path === 'templates')) {
            this.selectedTab = 'templates';
            const id = firstChild.params.id;
            if (id) {
                this.dataService
                    .getTemplate(id)
                    .pipe(
                        concatMap((template) =>
                            openQuoteCreationDialog(this.dialog, {
                                title: this.translateService.instant('OVERVIEW.DIALOG.USE_TEMPLATE.TEMPLATE_TITLE', {
                                    title: template.customerPurchaseNumber,
                                }),
                                confirm: this.translateService.instant('OVERVIEW.DIALOG.USE_TEMPLATE.CONFIRM'),
                                cancel: this.translateService.instant('COMMON.DIALOG.CANCEL'),
                                description: this.translateService.instant('OVERVIEW.DIALOG.USE_TEMPLATE.DESCRIPTION'),
                                templateDescription: this.translateService.instant(
                                    'OVERVIEW.DIALOG.USE_TEMPLATE.TEMPLATE_DESCRIPTION',
                                    { description: template.templateDescription }
                                ),
                            })
                        )
                    )
                    .subscribe({
                        next: (customerPurchaseNumber) => {
                            if (customerPurchaseNumber) {
                                this.onUseTemplate({ id, status: QuoteStatus.new, customerPurchaseNumber });
                            }
                        },
                        error: () => {
                            this.snackBarService.openSnackBar({
                                message: this.translateService.instant('OVERVIEW.DIALOG.USE_TEMPLATE.FAILURE_MSG'),
                                isFailure: true,
                            });
                        },
                    });
            }
        } else {
            this.selectedTab = 'offers';
        }
    }

    public get isPartnerMode() {
        return !!this.selectedCustomerService.customerForPartnerMode$?.value;
    }

    public get isEmployee() {
        return this.permissionService.isUserAnEmployee;
    }

    public get isAdmin() {
        return this.permissionService.isCurrentUserAdmin();
    }

    public get showTemplatesTab() {
        return this.appService.isAuthorizedToUseTemplates;
    }

    public get isLoading() {
        return this.selectedTab === 'offers' ? this.isQuotesLoading : this.isTemplatesLoading;
    }

    public onViewQuote({ id, status, quoteId }: ViewQuoteEvent) {
        this.quoteDiscountsService.clearQuoteDiscounts();

        if (status && status !== QuoteStatus.new) {
            this.router.navigate(['next-steps'], {
                queryParams: { id, sapDocumentId: quoteId },
            });
        } else {
            this.navigateToConfiguration(id);
        }
    }

    public onDeleteQuote(event: DeleteQuoteEvent) {
        this.isQuotesLoading = true;
        this.deleteQuote(event);
    }

    public onConvertQuote(event: ConvertQuoteEvent) {
        this.dataService.updateQuote({ ...event, type: 'template' }).subscribe(() => {
            this.snackBarService.openSnackBar({
                message: this.translateService.instant('OVERVIEW.CONVERT.SUCCESS'),
                isFailure: false,
            });

            this.getTemplateList();
            this.getQuoteList();
        });
    }

    private setLoaderText(key: string) {
        // use instant first to avoid visible key while translations are being fetched
        this.loaderText = this.translateService.instant(key);
        // this makes sure we use the right language right after selecting a language from dropdown
        this.translateService.get(key).subscribe((res) => (this.loaderText = res));
    }

    public onCopyQuote(event: CopyQuoteEvent) {
        this.setLoaderText('OVERVIEW.LOADER.COPYING_OFFER');
        this.isQuotesLoading = true;
        this.copyQuote(event);
    }

    public onUseTemplate(event: CopyQuoteEvent) {
        this.setLoaderText('OVERVIEW.LOADER.CREATING_OFFER');
        this.isTemplatesLoading = true;
        this.useTemplate(event);
    }

    public onEditTemplate(event: EditTemplateEvent) {
        this.dataService.updateQuote(event).subscribe({
            next: () => {
                this.onSuccess('COMMON.OVERVIEW.EDIT.SUCCESS');
                this.getTemplateList();
            },
            error: () => this.onError('COMMON.OVERVIEW.EDIT.FAILURE'),
        });
    }

    public onDeleteTemplate({ id }: TemplateEvent) {
        this.dataService.updateQuote({ id, type: 'quote' }).subscribe({
            next: () => {
                this.onSuccess('COMMON.OVERVIEW.DELETE.SUCCESS');
                this.getTemplateList();
                this.getQuoteList();
            },
            error: () => this.onError('COMMON.OVERVIEW.DELETE.FAILURE'),
        });
    }

    public createNewOffer() {
        this.setLoaderText('OVERVIEW.LOADER.CREATING_OFFER');
        this.isQuotesLoading = true;

        this.dataService.createNew().subscribe({
            next: (response) => {
                this.quoteDiscountsService.clearQuoteDiscounts();
                this.onSuccess('OVERVIEW.SNACK_BAR.CREATE_OFFER.SUCCESS');
                this.navigateToConfiguration(response.id);
            },
            error: () => this.onError('OVERVIEW.SNACK_BAR.CREATE_OFFER.FAILURE'),
        });
    }

    public onSearch(event: SearchEvent) {
        if (this.selectedTab === 'offers') {
            this.isQuotesLoading = true;
            this.searchForQuotes(event);
        } else {
            this.isTemplatesLoading = true;
            this.searchForTemplates(event);
        }
    }

    private deleteQuote(params: DeleteQuoteEvent) {
        return this.dataService
            .delete(params)
            .pipe(delay(100))
            .subscribe({
                next: () => {
                    this.onSuccess('OVERVIEW.SNACK_BAR.DELETE_OFFER.SUCCESS');
                    this.getQuoteList();
                },
                error: () => this.onError('OVERVIEW.SNACK_BAR.DELETE_OFFER.FAILURE'),
            });
    }

    public getQuoteList() {
        this.setLoaderText('OVERVIEW.LOADER.LOADING_OFFERS');
        this.overviewTableDataSource.resetPaging();
        this.overviewTableDataSource
            .loadOverviewData()
            .pipe(catchError(() => of(this.onError('OVERVIEW.SNACK_BAR.FETCH_OFFERS.FAILURE'))))
            .subscribe();
    }

    public getTemplateList() {
        this.templatesDataSource.resetPaging();
        this.templatesDataSource
            .loadOverviewData()
            .pipe(catchError(() => of(this.onError('OVERVIEW.SNACK_BAR.FETCH_TEMPLATES.FAILURE'))))
            .subscribe();
    }

    public onPagingError() {
        this.onError('OVERVIEW.SNACK_BAR.PAGING.FAILURE');
    }

    public onSortError() {
        this.onError('OVERVIEW.SNACK_BAR.SORTING.FAILURE');
    }

    public onCustomerSelected() {
        this.getQuoteList();
    }

    private searchForQuotes(event: SearchEvent): void {
        this.overviewTableDataSource.resetPaging();

        this.overviewTableDataSource
            .loadOverviewData({ parameters: event })
            .pipe(catchError(() => of(this.onError('OVERVIEW.SNACK_BAR.SEARCH_OFFER.FAILURE'))))
            .subscribe();
    }

    private searchForTemplates(event: SearchEvent) {
        this.templatesDataSource.resetPaging();
        this.templatesDataSource
            .loadOverviewData({
                parameters: {
                    ...event,
                    sortBy: event.sortBy || 'description',
                    sortOrder: event.sortOrder || 'asc',
                },
                pageSize: 50,
            })
            .pipe(catchError(() => of(this.onError('OVERVIEW.SNACK_BAR.SEARCH_TEMPLATE.FAILURE'))))
            .subscribe();
    }

    private copyQuote(event: CopyQuoteEvent) {
        this.dataService.copy(event).subscribe({
            next: (response) => {
                this.onSuccess('OVERVIEW.SNACK_BAR.COPY_OFFER.SUCCESS');
                this.navigateToConfiguration(response.id);
            },
            error: () => this.onError('OVERVIEW.SNACK_BAR.COPY_OFFER.FAILURE'),
        });
    }

    private useTemplate(params: CopyQuoteParams) {
        this.dataService.useTemplate(params).subscribe({
            next: (response) => {
                this.onSuccess('OVERVIEW.SNACK_BAR.USE_TEMPLATE.SUCCESS');
                this.navigateToConfiguration(response.id);
            },
            error: () => this.onError('OVERVIEW.SNACK_BAR.USE_TEMPLATE.FAILURE'),
        });
    }

    private navigateToConfiguration(id: string) {
        this.quoteDiscountsService.clearQuoteDiscounts();
        this.router.navigate(['configuration'], { queryParams: { id } });
    }

    private onError(translationKey: string) {
        this.isQuotesLoading = false;
        this.isTemplatesLoading = false;
        if (this.permissionService.isUserAuthorized$.value) {
            setTimeout(() => {
                this.snackBarService.openSnackBar({
                    message: this.translateService.instant(translationKey),
                    isFailure: true,
                });
            }, 200);
        }
    }

    private onSuccess(translationKey: string) {
        this.snackBarService.openSnackBar({
            message: this.translateService.instant(translationKey),
            isFailure: false,
        });
    }
}
